import { useEffect, useRef } from 'react';
import { db } from '@/modules/shared/lib/db-client';
import { useAuth } from './useAuth';

// Update activity every 5 minutes while user is active
const ACTIVITY_UPDATE_INTERVAL = 5 * 60 * 1000;
// Consider user inactive after 1 minute of no interaction
const INACTIVITY_THRESHOLD = 60 * 1000;

export function useActivityTracker() {
  const { user } = useAuth();
  const lastActivityRef = useRef<number>(Date.now());
  const lastUpdateRef = useRef<number>(0);

  useEffect(() => {
    if (!user) return;

    const updateActivity = async () => {
      const now = Date.now();
      
      // Only update if user was active recently and hasn't updated in the interval
      if (
        now - lastActivityRef.current < INACTIVITY_THRESHOLD &&
        now - lastUpdateRef.current >= ACTIVITY_UPDATE_INTERVAL
      ) {
        lastUpdateRef.current = now;
        
        await db
          .from('professionals')
          .update({ last_activity_at: new Date().toISOString() } as any)
          .eq('user_id', user.id);
      }
    };

    // Track user interactions
    const handleActivity = () => {
      const now = Date.now();
      const wasInactive = now - lastActivityRef.current >= INACTIVITY_THRESHOLD;
      lastActivityRef.current = now;
      
      // If user was inactive and becomes active again, update immediately
      if (wasInactive && now - lastUpdateRef.current >= ACTIVITY_UPDATE_INTERVAL) {
        updateActivity();
      }
    };

    // Update on mount (user just became active)
    db
      .from('professionals')
      .update({ last_activity_at: new Date().toISOString() } as any)
      .eq('user_id', user.id)
      .then(() => {
        lastUpdateRef.current = Date.now();
      });

    // Listen for user interactions
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Periodic check
    const intervalId = setInterval(updateActivity, ACTIVITY_UPDATE_INTERVAL);

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearInterval(intervalId);
    };
  }, [user]);
}
